"use client";

import { ArrowRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mt-10 border border-border bg-secondary/60 p-8 text-center">
        <p className="font-default text-lg text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-foreground text-foreground hover:bg-foreground hover:text-background">
          Register Another Email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mt-10 space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[11px] font-medium uppercase tracking-widest text-foreground/60">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="h-12 border-0 border-b border-foreground/30 bg-transparent px-0 font-default text-base italic text-foreground placeholder:text-foreground/40 focus-visible:border-b-2 focus-visible:border-ring focus-visible:ring-0 focus-visible:[box-shadow:0_1px_0_0_var(--ring)]" {...field} />
              </FormControl>
              <FormMessage className="font-default text-sm italic text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-[11px] font-medium uppercase tracking-widest text-foreground/60">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 555-123-4567" className="h-12 border-0 border-b border-foreground/30 bg-transparent px-0 font-default text-base italic text-foreground placeholder:text-foreground/40 focus-visible:border-b-2 focus-visible:border-ring focus-visible:ring-0 focus-visible:[box-shadow:0_1px_0_0_var(--ring)]" {...field} />
              </FormControl>
              <FormMessage className="font-default text-sm italic text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-[11px] font-medium uppercase tracking-widest text-foreground/60">
              I'm Interested In
            </FormLabel>
            <FormControl>
              <select {...field} className="h-12 w-full border-0 border-b border-foreground/30 bg-transparent px-0 font-default text-base italic text-foreground placeholder:text-foreground/40 focus-visible:border-b-2 focus-visible:border-ring focus-visible:outline-none">
                <option value="">Select an option</option>
                <option value="New Fragrance Drops">New Fragrance Drops</option>
                <option value="Private Consultations">Private Consultations</option>
                <option value="Exclusive Events">Exclusive Events</option>
                <option value="All of the above">All of the above</option>
              </select>
            </FormControl>
            <FormMessage className="font-default text-sm italic text-destructive" />
          </FormItem>} />

      <div className="flex flex-col gap-4 pt-2 md:flex-row md:items-end">
        <div className="flex-1">
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans text-[11px] font-medium uppercase tracking-widest text-foreground/60">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="your@email.com" className="h-14 border-0 border-b border-foreground/40 bg-transparent px-0 font-default text-lg italic text-foreground placeholder:text-foreground/40 focus-visible:border-b-2 focus-visible:border-ring focus-visible:ring-0 focus-visible:[box-shadow:0_1px_0_0_var(--ring)]" {...field} />
                </FormControl>
                <FormMessage className="font-default text-sm italic text-destructive" />
              </FormItem>} />
        </div>

        <Button type="submit" disabled={isSubmitting} className="h-14 bg-foreground px-8 font-sans text-sm font-bold uppercase tracking-widest text-background shadow-md transition-all hover:bg-primary hover:text-primary-foreground disabled:opacity-60">
          {isSubmitting ? "Sending..." : "Subscribe"}
          <ArrowRight className="ml-1 size-4" />
        </Button>
      </div>

      {submitError && <div className="border border-destructive bg-destructive/10 p-4">
          <p className="font-default text-sm italic text-destructive">{submitError}</p>
        </div>}
    </Form>;
}
export default function JoinTheInnerCircle() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="2725" id="join-the-inner-circle" className="dark bg-background text-foreground relative overflow-hidden px-4 py-24 md:py-32 lg:py-[140px]">
      <div className="pointer-events-none absolute -right-32 top-1/2 -translate-y-1/2 select-none opacity-[0.08]" aria-hidden="true">
        <Zap className="size-[600px] text-primary md:size-[800px] lg:size-[1000px]" strokeWidth={1} fill="currentColor" />
      </div>

      <div className="pointer-events-none absolute right-0 top-0 h-full w-full select-none opacity-[0.05]" aria-hidden="true">
        <div className="absolute -right-20 top-0 h-[150%] w-1 rotate-12 bg-primary" />
      </div>

      <div ref={ref} className={`relative z-10 mx-auto w-full max-w-[720px] text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <p className="font-sans text-xs font-medium uppercase tracking-[0.3em] text-foreground/70">
          06 / Join The Inner Circle
        </p>

        <h2 className="font-default mt-8 text-foreground" style={{
        fontSize: "clamp(3rem, 6vw, 5rem)",
        lineHeight: 1.05
      }}>
          <span className="font-bold tracking-tight">Be the first to</span>
          <br />
          <span className="font-default font-light italic tracking-tight">experience.</span>
        </h2>

        <p className="font-default mx-auto mt-8 max-w-[580px] text-lg leading-relaxed text-foreground/70 md:text-[18px]">
          Receive early access to limited-edition fragrance releases, private event invitations, and curated olfactory insights from Essence & Co.
        </p>

        <FormProvider formKey="inner_circle_signup_our_heritage" formId="inner_circle_signup_our_heritage" sectionName="join-the-inner-circle" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        interest: ""
      }} validationRules={{
        full_name: {
          required: false
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: false,
          pattern: "phone"
        },
        interest: {
          required: false
        }
      }} submitText="Subscribe" successMessage="You are now part of our inner circle. Watch your inbox for exclusive invitations." errorMessage="Subscription failed. Please try again.">
          <NewsletterForm />
        </FormProvider>

        <p className="font-sans mt-8 text-[11px] uppercase tracking-[0.25em] text-foreground/50">
          No spam. Unsubscribe any time. Privacy respected.
        </p>
      </div>
    </section>;
}